/**
 * Light or dark, chosen once and remembered everywhere.
 *
 * TWO states. There was a third - "system" - and it was wrong for this
 * product: flipping a light switch took up to three taps, because the cycle
 * ran system -> light -> dark -> system and you could never be sure which
 * third of it you were standing in. A clerk in a dark booth wants dark, now,
 * with one tap.
 *
 * The operating system still gets the FIRST word. Until somebody expresses a
 * preference nothing is stored, and the page follows prefers-color-scheme. The
 * first tap is what makes it explicit, and from then on the console obeys the
 * person rather than the laptop.
 *
 * The preference is stored under one key and read by every page, which is what
 * makes it persist across the console: the clerk picks dark at the ring and
 * checkout is dark when they walk to the office.
 *
 * The pre-paint half lives in each page's <head> (see THEME_BOOT). It has to,
 * because a module script runs after first paint and the flash of the wrong
 * scheme is exactly what somebody notices.
 */

import { applyHostBrand } from './brand.js';

export type ThemeChoice = 'light' | 'dark';

const KEY = 'hn-theme';

/**
 * The snippet every page runs in <head>, before any stylesheet paints.
 *
 * Kept here as the single source of the string so the copies cannot drift
 * apart. Deliberately tiny and dependency-free: it is inline in a <script> tag
 * and runs before anything else on the page exists. An absent value is left
 * absent so the CSS media query decides, which is the no-preference case.
 */
export const THEME_BOOT =
  `try{var t=localStorage.getItem('${KEY}');if(t==='light'||t==='dark')document.documentElement.dataset.theme=t;}catch(e){}`;

/** What the OS asks for, when nobody has said otherwise. */
export function systemTheme(): ThemeChoice {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** The stored preference, or null while the OS is still deciding. */
export function storedTheme(): ThemeChoice | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    // Storage can be denied (private mode, embedded webview). The console must
    // still render, so an unreadable preference is simply "not chosen".
    return null;
  }
}

/** The theme actually on screen right now. */
export function readTheme(): ThemeChoice {
  return storedTheme() ?? systemTheme();
}

export function applyTheme(choice: ThemeChoice): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset['theme'] = choice;
  try {
    localStorage.setItem(KEY, choice);
  } catch { /* not fatal: the choice applies for this page either way */ }
}

/** The other one. That is the whole state machine now. */
export const nextTheme = (c: ThemeChoice): ThemeChoice => (c === 'dark' ? 'light' : 'dark');

const LABEL: Record<ThemeChoice, { ico: string; txt: string; title: string }> = {
  light: { ico: '☀', txt: 'Light', title: 'Theme: light. Click for dark.' },
  dark: { ico: '☾', txt: 'Dark', title: 'Theme: dark. Click for light.' },
};

/**
 * Mount the toggle into a host element.
 *
 * Also listens for changes from OTHER TABS. A clerk with the ring on one
 * screen and checkout on another changed the theme in one of them and meant
 * both; `storage` fires in every other tab of the origin, so both follow.
 */
export function mountThemeToggle(host: HTMLElement | null): void {
  if (!host || typeof document === 'undefined') return;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'hn-theme';
  const ico = document.createElement('span');
  ico.className = 'ico';
  ico.setAttribute('aria-hidden', 'true');
  const txt = document.createElement('span');
  txt.className = 'txt';
  btn.append(ico, txt);

  const paint = (c: ThemeChoice): void => {
    const l = LABEL[c];
    ico.textContent = l.ico;
    txt.textContent = l.txt;
    btn.title = l.title;
    // The state is in the label, not only in the glyph (brand skill §20).
    btn.setAttribute('aria-label', l.title);
  };

  paint(readTheme());
  btn.addEventListener('click', () => {
    const next = nextTheme(readTheme());
    applyTheme(next);
    paint(next);
  });

  window.addEventListener('storage', (e) => {
    if (e.key !== KEY) return;
    const c = readTheme();
    applyTheme(c);
    paint(c);
  });

  host.appendChild(btn);
}

/**
 * Apply the stored preference and mount the control, in one call.
 *
 * Entry modules call this at module scope, and those modules are imported by
 * tests that run in plain Node with no DOM. A no-op there rather than a
 * `ReferenceError: document is not defined` - a theme control is not a reason
 * for a unit test to be unable to import the module it is testing.
 *
 * Only an EXPRESSED preference is applied. With nothing stored, the root is
 * left alone so prefers-color-scheme keeps control and a laptop that flips at
 * sunset still flips the console with it.
 */
export function initTheme(hostId = 'theme-slot'): void {
  if (typeof document === 'undefined') return;
  const stored = storedTheme();
  if (stored) applyTheme(stored);
  mountThemeToggle(document.getElementById(hostId));
  // The one boot hook every staff page already runs, so the host brand rides it
  // rather than twelve new imports (ADR-69 section 13).
  applyHostBrand();
}
